export type CaseStudySectionType = 
  | "introduction"
  | "problem-statement" 
  | "goals-objectives"
  | "design-process"
  | "key-decisions"
  | "outcome-impact"
  | "learnings-reflections"
  | "conclusion"
  | "role-contribution"
  | "one-liner"
  | "custom";

export type ContentBlockType =
  | "text"
  | "image"
  | "video"
  | "gallery"
  | "carousel"
  | "line-break"
  | "quote"
  | "callout";

export interface CaseStudySection {
  id: string;
  type: CaseStudySectionType;
  order: number;
  title: string;
  content: string; // Rich text content (legacy)
  images: CaseStudyImage[];
  content_blocks?: ContentBlock[]; // New modular content blocks
}

export interface CaseStudyImage {
  id: string;
  url: string;
  caption?: string;
  order: number;
}

export interface ContentBlock {
  id: string;
  type: ContentBlockType;
  content: Record<string, unknown>; // Type varies by block type
  order: number;
}

export interface CaseStudyItem {
  id: string;
  title: string;
  description: string;
  cover_image: string;
  sections: CaseStudySection[];
  created_at: string;
  updated_at?: string;
  is_featured?: boolean;
  status?: "draft" | "published";
  order?: number;
  slug?: string;
  seo_title?: string;
  seo_description?: string;
  og_image?: string;
  profile_id?: string;
  profile?: {
    username?: string | null;
    display_name?: string | null;
    avatar_url?: string | null;
    username_slug?: string | null;
  };
}

// Section configuration for UI
export const CASE_STUDY_SECTIONS = [
  {
    type: "introduction" as const,
    title: "Introduction",
    description: "Set the context and background for your project",
    icon: "📖",
  },
  {
    type: "problem-statement" as const,
    title: "Problem Statement",
    description: "Define the challenge you were solving",
    icon: "🎯",
  },
  {
    type: "goals-objectives" as const,
    title: "Goals & Objectives",
    description: "What you aimed to achieve",
    icon: "🎯",
  },
  {
    type: "design-process" as const,
    title: "Design Process",
    description: "Your methodology and approach",
    icon: "🔄",
  },
  {
    type: "key-decisions" as const,
    title: "Key Decisions & Trade-offs",
    description: "Important choices and their rationale",
    icon: "⚖️",
  },
  {
    type: "outcome-impact" as const,
    title: "Outcome & Impact",
    description: "Results and measurable impact",
    icon: "📈",
  },
  {
    type: "learnings-reflections" as const,
    title: "Learnings & Reflections",
    description: "What you learned and would do differently",
    icon: "💡",
  },
  {
    type: "conclusion" as const,
    title: "Conclusion",
    description: "Wrap up and key takeaways",
    icon: "🏁",
  },
  {
    type: "role-contribution" as const,
    title: "Role & Contribution",
    description: "Your involvement and responsibilities",
    icon: "👥",
  },
  {
    type: "one-liner" as const,
    title: "One-liner Summary",
    description: "Brief summary of the project",
    icon: "T",
  },
] as const;

// Content type definitions for sidebar
export const CONTENT_TYPES = [
  {
    type: "text" as const,
    title: "Text Section",
    description: "Add rich text content",
    icon: "Type",
    color: "bg-purple-100 text-purple-600",
    removable: true, // Can be deleted
  },
  {
    type: "image" as const,
    title: "Image, Video, GIF",
    description: "Upload media files",
    icon: "Image",
    color: "bg-green-100 text-green-600",
    removable: true, // Can be deleted
  },
  {
    type: "gallery" as const,
    title: "Image Gallery",
    description: "Create image galleries",
    icon: "Grid3X3",
    color: "bg-blue-100 text-blue-600",
    removable: true, // Can be deleted
  },
  {
    type: "carousel" as const,
    title: "Carousel",
    description: "Create image carousels",
    icon: "Images",
    color: "bg-indigo-100 text-indigo-600",
    removable: true, // Can be deleted
  },
  {
    type: "line-break" as const,
    title: "Line Break",
    description: "Add spacing",
    icon: "Minus",
    color: "bg-gray-100 text-gray-600",
    removable: true, // Can be deleted
  },
  {
    type: "quote" as const,
    title: "Quote Block",
    description: "Add styled quotes",
    icon: "Quote",
    color: "bg-orange-100 text-orange-600",
    removable: true, // Can be deleted
  },
  {
    type: "callout" as const,
    title: "Callout",
    description: "Add highlighted callout boxes",
    icon: "Info",
    color: "bg-yellow-100 text-yellow-600",
    removable: true, // Can be deleted
  },
] as const;

// Utility function to check if a content block type is removable
export function isContentBlockRemovable(blockType: ContentBlockType): boolean {
  const contentType = CONTENT_TYPES.find(ct => ct.type === blockType);
  return contentType?.removable ?? true; // Default to true if not specified
}

// Project type for portfolio items
export type ProjectType = "shot" | "case-study";